// Catalog.ID Client — Express Trust Pane
// ============================================================================
// Allows rating another identity (from profile lookup or trust workspace).

import {
    createElement, createFieldGroup, createFormRow, createEmptyState, renderPaneActions,
} from '@catalog-shell';
import type {
    PaneActionDescriptor, PaneInstance, PaneType, PaneTypeRenderContext,
} from '@catalog-shell';
import * as trustApi from '../api/trust';
import { generateTrustCommitment, signTrustExpression } from '../crypto/trust';
import { identity } from '../state/identity';
import { PANE_IDS } from '../shell-config';

const SCORE_OPTIONS: { value: number; label: string }[] = [
    { value: 1, label: '1 — Distrust' },
    { value: 2, label: '2 — Low' },
    { value: 3, label: '3 — Neutral' },
    { value: 4, label: '4 — Trust' },
    { value: 5, label: '5 — High Trust' },
];

// ----------------------------------------------------------------------------
// Title-row action
//
// Submit is the verb; the score buttons and the subgraph checkbox are the values
// it submits, so they stay in the body (shell USAGE, "Where a button belongs").
// No score is pre-selected: this pane is also reached from "Update Your Rating",
// where an assumed default would quietly overwrite an existing rating with a
// number the rater never picked — so Submit stays disabled, and says so, until
// one of the five is chosen.
// ----------------------------------------------------------------------------

/** What the title row needs to know about the rating being composed. */
export interface TrustExpressActionState {
    /** The form is on screen (a personal identity with a target). */
    canRate: boolean;
    /** The chosen score, or null while none has been picked. */
    score: number | null;
    busy: boolean;
}

interface TrustExpressState extends TrustExpressActionState {
    ctx: PaneTypeRenderContext;
    targetCid: string;
    subgraphVisible: () => boolean;
    showError: (message: string) => void;
    showSuccess: (message: string) => void;
}

const states = new WeakMap<PaneInstance, TrustExpressState>();

/** Pure: the single verb, and what it is waiting for. */
export function planTrustExpressActions(
    st: TrustExpressActionState | undefined,
    exec: () => void,
): PaneActionDescriptor[] {
    if (!st?.canRate) return [];
    const blocked = st.score === null
        ? 'Choose a rating from 1 to 5 first'
        : st.busy ? 'Submitting your rating…' : undefined;
    return [{
        key: 'submit',
        label: 'Submit Rating',
        variant: 'primary',
        disabled: Boolean(blocked),
        title: blocked,
        execute: () => exec(),
    }];
}

function trustExpressActions(instance: PaneInstance): HTMLElement[] {
    const st = states.get(instance);
    return renderPaneActions(planTrustExpressActions(st, () => { void submitRating(st!); }));
}

async function submitRating(st: TrustExpressState): Promise<void> {
    if (st.busy || st.score === null) return;
    const { ctx, targetCid, score } = st;
    st.busy = true;
    st.showError('');
    st.showSuccess('');
    ctx.shell.refreshPaneActions(ctx.instance.instanceId);
    try {
        const sourceCid = identity.getActiveCid()!;
        const { nonce, commitment } = await generateTrustCommitment(sourceCid);
        const signature = signTrustExpression(targetCid, score);

        const result = await trustApi.expressTrust(
            targetCid, score, signature, commitment, nonce, st.subgraphVisible(),
        );

        st.showSuccess(result.updated ? 'Trust expression updated.' : 'Trust expression created.');
    } catch (e) {
        st.showError(e instanceof Error ? e.message : 'Failed to submit');
    } finally {
        st.busy = false;
        ctx.shell.refreshPaneActions(ctx.instance.instanceId);
    }
}

export function createTrustExpressPane(): PaneType {
    return {
        typeId: PANE_IDS.trustExpress,
        kind: 'leaf',
        defaultTitle: 'Rate Identity',
        capabilities: [],
        tabBarActions: trustExpressActions,
        onDestroy: (instance) => { states.delete(instance); },
        render: (ctx: PaneTypeRenderContext) => {
            const container = createElement('div', { className: 'cid-pane-content' });
            const targetCid = ctx.instance.context['cid'] as string | undefined;
            if (!targetCid) {
                container.appendChild(createEmptyState({ message: 'No target identity specified' }));
                return container;
            }
            const st: TrustExpressState = {
                ctx,
                targetCid,
                canRate: false,
                score: null,
                busy: false,
                subgraphVisible: () => true,
                showError: () => {},
                showSuccess: () => {},
            };
            states.set(ctx.instance, st);
            void renderExpress(container, st);
            return container;
        },
    };
}

async function renderExpress(container: HTMLElement, st: TrustExpressState): Promise<void> {
    const { ctx, targetCid } = st;
    if (!identity.isPersonal()) {
        container.appendChild(createEmptyState({ message: 'Only personal identities can rate others' }));
        ctx.shell.refreshPaneActions(ctx.instance.instanceId);
        return;
    }

    const formGroup = createFieldGroup({ title: 'Express Trust' });

    const cidRow = createFormRow({ label: 'Target' });
    cidRow.content.appendChild(createElement('span', {
        className: 'cid-profile-cid',
        textContent: targetCid,
    }));
    formGroup.body.appendChild(cidRow.element);

    // Score selector
    const scoreRow = createFormRow({ label: 'Rating' });
    const btnContainer = createElement('div', { className: 'cid-trust-score-buttons' });

    const buttons: HTMLButtonElement[] = [];
    for (const opt of SCORE_OPTIONS) {
        const btn = createElement('button', {
            className: 'cs-btn cs-btn--small cs-btn--default',
            textContent: opt.label,
        }) as HTMLButtonElement;
        btn.addEventListener('click', () => {
            const wasUnset = st.score === null;
            st.score = opt.value;
            buttons.forEach((b, i) => {
                b.className = `cs-btn cs-btn--small ${SCORE_OPTIONS[i].value === st.score ? 'cs-btn--yes' : 'cs-btn--default'}`;
            });
            // Only the first choice changes what the row says.
            if (wasUnset) ctx.shell.refreshPaneActions(ctx.instance.instanceId);
        });
        buttons.push(btn);
        btnContainer.appendChild(btn);
    }
    scoreRow.content.appendChild(btnContainer);
    formGroup.body.appendChild(scoreRow.element);

    // Subgraph visibility
    const visRow = createFormRow({ label: 'Subgraph Visible' });
    const visCheckbox = createElement('input', {}) as HTMLInputElement;
    visCheckbox.type = 'checkbox';
    visCheckbox.checked = true;
    const visLabel = createElement('label', { className: 'cid-pane-row' });
    visLabel.appendChild(visCheckbox);
    visLabel.appendChild(createElement('span', { textContent: 'Include in verifiable subgraph' }));
    visRow.content.appendChild(visLabel);
    formGroup.body.appendChild(visRow.element);

    // The submit button itself is in the title row; these two lines report what
    // came back from it.
    const errorEl = createElement('div', { className: 'cid-pane-error' });
    const successEl = createElement('div', { className: 'cid-trust-success' });
    formGroup.body.appendChild(errorEl);
    formGroup.body.appendChild(successEl);

    container.appendChild(formGroup.element);

    st.subgraphVisible = () => visCheckbox.checked;
    st.showError = (message) => {
        errorEl.textContent = message;
        errorEl.classList.toggle('cid-pane-error--visible', Boolean(message));
    };
    st.showSuccess = (message) => { successEl.textContent = message; };
    st.canRate = true;
    ctx.shell.refreshPaneActions(ctx.instance.instanceId);
}
